import { type FormEvent, useState } from "react";

import { Tetromino } from "@/components/Tetromino";
import { adminApi } from "@/lib/adminApi";
import { Field, StatusBanner } from "@/components/ui";
import { useSaveState } from "@/lib/useSaveState";

interface LoginPanelProps {
  readonly onSignedIn: () => void;
  /** False when the server has no account at all — worth saying so plainly. */
  readonly configured: boolean;
}

export function LoginPanel({ onSignedIn, configured }: LoginPanelProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const save = useSaveState();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const result = await save.run(() => adminApi.login(username, password));
    if (result) onSignedIn();
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 py-12">
      <div className="w-full max-w-[26rem]">
        <div className="mb-6 flex items-center gap-3">
          <Tetromino type="J" cell={13} title="J piece" />
          <div>
            <h1 className="text-[1.75rem] leading-none">Club admin</h1>
            <p className="meta mt-1.5 opacity-60">Tetris At UCI</p>
          </div>
        </div>

        <form onSubmit={submit} className="panel offset-ink p-6">
          {!configured ? (
            <div className="mb-5">
              <StatusBanner
                tone="error"
                message={
                  "No admin account exists yet. On the server, run: " +
                  "python -m tetrisatuci.admin_cli add <username>"
                }
              />
            </div>
          ) : null}

          <div className="flex flex-col gap-4">
            <Field label="Username" htmlFor="admin-username">
              <input
                id="admin-username"
                className="input"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </Field>

            <Field label="Password" htmlFor="admin-password">
              <input
                id="admin-password"
                className="input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
          </div>

          {save.message ? (
            <div className="mt-4">
              <StatusBanner
                tone={save.tone === "ok" ? "ok" : "error"}
                message={save.tone === "error" ? save.message : "Signed in."}
              />
            </div>
          ) : null}

          <button
            type="submit"
            className="btn mt-6 w-full justify-center"
            disabled={save.saving || !configured}
          >
            {save.saving ? "Checking…" : "Sign in"}
          </button>
        </form>

        <p className="mt-4 text-[0.8125rem] leading-relaxed opacity-55">
          Sessions last twelve hours. Changing your password signs out every device.
        </p>
      </div>
    </div>
  );
}
